/**
 * Voice and language options for the comparison-video narrator.
 *
 * Voices are Gemini's prebuilt TTS voices, selected via
 * `speechConfig.voiceConfig.prebuiltVoiceConfig.voiceName`. This is a curated
 * subset of the full list — enough range without turning the picker into a wall.
 *
 * Language is NOT set through the API: native-audio Live models pick the
 * language themselves and reject an explicit `languageCode`. So the chosen
 * language is enforced in the system instruction instead (see prompt.ts).
 */

export interface VoiceOption {
	name: string;
	description: string;
}

export const VOICES: VoiceOption[] = [
	{ name: "Puck", description: "Upbeat" },
	{ name: "Charon", description: "Informative" },
	{ name: "Kore", description: "Firm" },
	{ name: "Zephyr", description: "Bright" },
	{ name: "Aoede", description: "Breezy" },
	{ name: "Leda", description: "Youthful" },
	{ name: "Fenrir", description: "Excitable" },
	{ name: "Orus", description: "Firm" },
	{ name: "Callirrhoe", description: "Easy-going" },
	{ name: "Achird", description: "Friendly" },
	{ name: "Sulafat", description: "Warm" },
	{ name: "Sadaltager", description: "Knowledgeable" },
];

export const DEFAULT_VOICE = "Puck";

export interface LanguageOption {
	/** BCP-47 code — used as the option value and for the `lang` attribute. */
	code: string;
	/** English name, which is what goes into the system instruction. */
	label: string;
}

export const LANGUAGES: LanguageOption[] = [
	{ code: "en", label: "English" },
	{ code: "hi", label: "Hindi" },
	{ code: "es", label: "Spanish" },
	{ code: "fr", label: "French" },
	{ code: "de", label: "German" },
	{ code: "pt", label: "Portuguese" },
	{ code: "it", label: "Italian" },
	{ code: "nl", label: "Dutch" },
	{ code: "pl", label: "Polish" },
	{ code: "ru", label: "Russian" },
	{ code: "ar", label: "Arabic" },
	{ code: "tr", label: "Turkish" },
	{ code: "id", label: "Indonesian" },
	{ code: "vi", label: "Vietnamese" },
	{ code: "ja", label: "Japanese" },
	{ code: "ko", label: "Korean" },
	{ code: "zh", label: "Chinese" },
	{ code: "bn", label: "Bengali" },
	{ code: "ta", label: "Tamil" },
	{ code: "te", label: "Telugu" },
	{ code: "mr", label: "Marathi" },
	{ code: "ur", label: "Urdu" },
];

export const DEFAULT_LANGUAGE = "English";

export function findLanguageLabel({ code }: { code: string }): string {
	return LANGUAGES.find((language) => language.code === code)?.label ?? DEFAULT_LANGUAGE;
}
